import { err, ok, type Result } from 'neverthrow'
import { COMPARISON_METHOD } from 'src/tpbp-core/models/common.const'
import type { RecordKey, Scalar } from 'src/tpbp-core/models/global.type'
import _ from 'underscore'

export function getIsBigger(valueA, valueB, isInsensitive = false) {
  if (valueA === valueB) return
  if (valueA === null || valueA === undefined) return false
  if (valueB === null || valueB === undefined) return true
  const aType = typeof valueA
  const bType = typeof valueB
  if (isInsensitive && aType === 'string' && bType === 'string') {
    return (
      valueA.toLowerCase().localeCompare() >
      valueB.toLowerCase().localeCompare()
    )
  }
  return aType === bType ? valueA > valueB : undefined
}

export function sortBy<T>(
  list: T[],
  key: keyof T,
  comparisonMethod?: COMPARISON_METHOD,
) {
  const isReverseOrder = comparisonMethod === COMPARISON_METHOD.reverseOrder
  const isInsensitive = comparisonMethod === COMPARISON_METHOD.insensitive
  const spin = isReverseOrder ? -1 : 1
  return list.sort((a, b) => {
    const valueA = a[key]
    const valueB = b[key]
    const isBigger = getIsBigger(valueA, valueB, isInsensitive)
    if (isBigger === undefined) return 0
    return isBigger ? spin : -spin
  })
}

export function scalarToString(value: Scalar) {
  return value.toString()
}

// inspired by lodash https://lodash.com/docs/#zipObject
// can't use underscore.object because it's not quite the same
export function zipObject<T = Record<string, unknown>>(
  list: Scalar[],
  value: unknown = null,
  initialObject = {} as Partial<T>,
) {
  const data = list.length > 0 ? list : Object.values(list)
  return data.reduce<Partial<T>>((zipped, key, index) => {
    const normalizedKey = scalarToString(key)
    zipped[normalizedKey] = typeof value === 'function' ? value(index) : value
    return zipped
  }, initialObject) as T
}

/**
 * Transforms Array to object
 * TODO : refactor this function, it's similar to underscore.indexBy but valueFunction doesn't exists in underscore implementation
 */
export function byKey<T, K>(
  list: T[],
  key: RecordKey<T>,
  valueFunction?: (parameter: T) => K,
) {
  return list.reduce<Record<keyof T, K>>(
    (byKey, item) => {
      const value = typeof key === 'function' ? key(item) : item[key]
      byKey[value] = valueFunction ? valueFunction(item) : item
      return byKey
    },
    {} as Record<keyof T, K>,
  )
}

export function sortByNullAfter<Type>(list: Type[], key?: string) {
  return list.sort((itemA, itemB) => {
    const a = key ? itemA[key] : itemA
    const b = key ? itemB[key] : itemB
    if (a === null && b === null) return 0
    if (a === null) return 1
    if (b === null) return -1
    return a > b ? 1 : -1
  })
}

export function isEqual(originalValue: unknown, currentValue: unknown) {
  if (bothAreObjects(originalValue, currentValue)) {
    return isSameObject(
      originalValue as Record<string, unknown>,
      currentValue as Record<string, unknown>,
    )
  }
  return currentValue === originalValue
}

function isSameObject<T extends Record<string, unknown>>(
  firstObject: T,
  secondObject: T,
): boolean {
  if (Array.isArray(firstObject) !== Array.isArray(secondObject)) return false

  const firstObjectKeys = Object.keys(firstObject)
  const secondObjectKeys = Object.keys(secondObject)

  if (firstObjectKeys.length !== secondObjectKeys.length) return false

  for (const key of firstObjectKeys) {
    if (!secondObjectKeys.includes(key)) return false

    const firstValue = firstObject[key]
    const secondValue = secondObject[key]

    if (
      bothAreObjects(firstValue, secondValue) &&
      !isSameObject(firstValue as T, secondValue as T)
    )
      return false

    if (!bothAreObjects(firstValue, secondValue) && firstValue !== secondValue)
      return false
  }

  return true
}

function bothAreObjects(firstValue: unknown, secondValue: unknown): boolean {
  return (
    typeof firstValue === 'object' &&
    typeof secondValue === 'object' &&
    firstValue !== null &&
    secondValue !== null
  )
}

function customDeepClone<T>(
  value: T,
  seen = new WeakMap<object, unknown>(),
): T {
  if (value === null || typeof value !== 'object') {
    return value
  }

  if (value instanceof Date) {
    return new Date(value.getTime()) as unknown as T
  }

  if (value instanceof RegExp) {
    return new RegExp(value.source, value.flags) as unknown as T
  }

  if (seen.has(value)) {
    return seen.get(value) as T
  }

  if (Array.isArray(value)) {
    const result: unknown[] = []
    seen.set(value, result)
    for (const item of value) {
      result.push(customDeepClone(item, seen))
    }
    return result as unknown as T
  }

  const result = {} as Record<string, unknown>
  seen.set(value, result)
  for (const [key, propertyValue] of Object.entries(value)) {
    if (typeof propertyValue !== 'function') {
      result[key] = customDeepClone(propertyValue, seen)
    }
  }

  return result as unknown as T
}

export function deepCopy<T>(originalValue: T): T {
  if (!originalValue) return originalValue
  try {
    return structuredClone(originalValue)
  } catch (error: unknown) {
    const errorObject = error as Record<string, unknown>
    const isCloneError =
      errorObject?.name === 'DataCloneError' ||
      errorObject?.name === 'DOMException' ||
      (typeof errorObject?.message === 'string' &&
        errorObject.message.includes('could not be cloned'))

    if (isCloneError) {
      return customDeepClone(originalValue)
    }
    throw error
  }
}

export function random(root: string) {
  const random = `${Math.random()}`.slice(3)
  return `${root}${random}`
}

export function sumByKey<Type>(list: Type[], key: RecordKey<Type>) {
  return list.reduce((sum, row) => {
    const value = typeof key === 'function' ? key(row) : row[key]
    return typeof value === 'number' ? sum + value : sum
  }, 0)
}

export function filterAndSumByKey<Type>(
  list: readonly Type[],
  filterKey: string | ((parameter: Type) => boolean),
  key: string | ((parameter: Type) => string | number),
) {
  const filteredList =
    typeof filterKey === 'function'
      ? list.filter((element) => filterKey(element))
      : list.filter((row) => row[filterKey])

  return sumByKey(filteredList, key)
}

export function uniqByKey<Type = unknown>(
  tab: Type[],
  key: string | ((parameter: unknown) => string),
) {
  return Object.values(_.indexBy(tab, key))
}

export function getKeyByValue(object: Record<string, unknown>, value: unknown) {
  return Object.keys(object).find((key) => object[key] === value)
}

export function stringToNumber(
  value: string,
  emptyValue = 0,
  disallowNegative = false,
) {
  if (typeof value === 'number') return value
  const regExp = disallowNegative ? /[^\d.]/g : /[^\d.-]/g
  const cleanedValue = value ? value.replace(/,/, '.').replace(regExp, '') : ''
  const isEmpty = cleanedValue === '' || cleanedValue === '.'
  return isEmpty ? emptyValue : Number.parseFloat(cleanedValue)
}

export function checkIfNumber(toTest: unknown): toTest is number {
  if (Number.isNaN(toTest)) return false
  return typeof toTest === 'number'
}

export function valueIsEmpty(value: unknown) {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object' || value === false || value === 0) return false
  return !value
}

export function getUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = Math.trunc(Math.random() * 16)
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

type NumberOrNull = number | null

export function getMinValue(
  list: (NumberOrNull | NumberOrNull[])[],
): NumberOrNull {
  return list.reduce((minValue: NumberOrNull, row) => {
    const value: NumberOrNull = Array.isArray(row) ? getMinValue(row) : row
    if (
      minValue === null ||
      (value && value < minValue && !valueIsEmpty(value))
    ) {
      return value
    }
    return minValue
  }, null)
}

function computeNumberWithCountryWithoutSpace(
  phoneWithoutSpecialCharExceptCountry: string,
) {
  if (/^0\d/.test(phoneWithoutSpecialCharExceptCountry)) {
    return `+33 ${phoneWithoutSpecialCharExceptCountry.slice(1)}`
  }
  return phoneWithoutSpecialCharExceptCountry
}

function addSpaceEveryTwoNumber(phone: string) {
  return phone.replace(/(\d)(?=(\d{2})+(?!\d))/g, '$1 ')
}

function convertToStringAndReplaceSpacesAndHyphens(value: string | number) {
  return `${value}`.replace(/\s/g, '').replace(/-/g, '')
}

export function formatPhone(value: string | number | undefined | null): string {
  if (!value) return ''
  const phoneWithoutSpecialCharExceptCountry =
    convertToStringAndReplaceSpacesAndHyphens(value)
  const phone = computeNumberWithCountryWithoutSpace(
    phoneWithoutSpecialCharExceptCountry,
  )
  return addSpaceEveryTwoNumber(phone)
}

export function invert(object: Record<string, string>) {
  return Object.keys(object).reduce((invert, key) => {
    const value = object[key] as string
    invert[value] = key
    return invert
  }, {})
}

export function retry<Type>(attempt: number, callback: () => Promise<Type>) {
  let numberOfTry = 0

  const onError = (errorCode: number) => {
    if (errorCode === 500) return err(new Error('server error'))
    numberOfTry += 1
    if (numberOfTry >= attempt) return err(new Error('maximum try reach'))
    return retryFunction()
  }

  const retryFunction = async (): Promise<Result<Type, Error>> => {
    try {
      const response = await callback()
      // !This should not work like this but it actually does
      // we need to check our axios config
      if (response instanceof Error) {
        const errorCode = response.cause as number
        return onError(errorCode || 0)
      }
      return ok(response)
    } catch (error) {
      const errorCode = (error as Error).cause as number
      return onError(errorCode || 0)
    }
  }
  const result = retryFunction()
  return result
}

/**
 * Transform the first letter of a string into capital
 * @param string `"hello for John"`
 * @returns `"Hello for John"`
 */
export function capitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function floorCent(number: number, precision = 100) {
  return Math.floor(number * precision) / precision
}

export function roundCent(number: number, precision = 100) {
  return Math.round(number * precision) / precision
}

export function getOldestDate(dates: string[]) {
  // eslint-disable-next-line unicorn/no-array-reduce
  return dates.reduce((accumulator, current) => {
    if (!current || !accumulator) {
      return current || accumulator
    }
    return Date.parse(current) < Date.parse(accumulator) ? current : accumulator
  })
}

export function removeHtmlTags(message: string) {
  return message.replace(/<\/?\w[^>]*>|&\w+;/gm, '')
}

export const isProduction = process.env.NODE_ENV === 'production'

export const isDevelopment = !isProduction

export function getComponentName(component: unknown) {
  const name = (component as { name: string })?.name ?? ''
  if (!name)
    throw new Error('Component has no name property, please provide one')
  return name
}

export function addKeyIfMissing(keys: string[], key: string) {
  if (!keys.includes(key)) {
    keys.push(key)
  }
}

export function removeKeyIfPresent(ids: string[], id: string) {
  const index = ids.indexOf(id)
  if (index >= 0) {
    ids.splice(index, 1)
  }
}

export function getEntries<Object_ extends object>(object: Object_) {
  return Object.entries(object) as Entries<Object_>
}

export function findAndSetFirst<T>(
  items: T[],
  predicate: (item: T) => boolean,
): T[] {
  const newItems = [...items]
  const index = newItems.findIndex((item) => predicate(item))
  const [found] = newItems.splice(index, 1)
  if (!found || index <= 0) return items
  newItems.unshift(found)
  return newItems
}
